export interface CsvColumn<T = Record<string, unknown>> {
  id: string
  label: string
  format?: (value: unknown, row: T) => string | number | null | undefined
}

export interface ExportCsvOptions<T = Record<string, unknown>> {
  filename: string
  columns: CsvColumn<T>[]
  rows: T[]
}

export const escapeCsvValue = (val: unknown): string => {
  if (val === null || val === undefined) return ''
  let str = String(val)
  const isNumber = typeof val === 'number' && Number.isFinite(val)
  const isNumericString = typeof val === 'string' && /^-?\d+(\.\d+)?$/.test(val)
  if (!isNumber && !isNumericString && /^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`
  }
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

/**
 * Triggers a browser download of CSV text with UTF-8 BOM (\uFEFF) for Excel compatibility.
 */
export const downloadCsvText = (filename: string, csvContent: string): void => {
  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`)
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Exports data to an RFC-4180 compliant CSV file and triggers a browser download.
 * Includes UTF-8 BOM (\uFEFF) for Microsoft Excel compatibility.
 */
export const exportCsv = <T = Record<string, unknown>>({
  filename,
  columns,
  rows,
}: ExportCsvOptions<T>): void => {
  const header = columns.map((col) => escapeCsvValue(col.label)).join(',')
  const dataRows = rows.map((row) =>
    columns
      .map((col) => {
        const raw = (row as Record<string, unknown>)[col.id]
        const formatted = col.format ? col.format(raw, row) : raw
        return escapeCsvValue(formatted)
      })
      .join(','),
  )
  const csvContent = [header, ...dataRows].join('\r\n')
  downloadCsvText(filename, csvContent)
}

/**
 * Parses raw CSV text into a 2D array of strings respecting RFC-4180 rules
 * (quoted strings, escaped double quotes, line breaks inside quotes).
 */
export const parseCsv = (text: string): string[][] => {
  const clean = text.replace(/^\uFEFF/, '') // Strip BOM if present
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentField = ''
  let inQuotes = false
  let i = 0

  while (i < clean.length) {
    const char = clean[i]

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < clean.length && clean[i + 1] === '"') {
          currentField += '"'
          i += 2
          continue
        } else {
          inQuotes = false
          i++
          continue
        }
      } else {
        currentField += char
        i++
        continue
      }
    } else {
      if (char === '"') {
        inQuotes = true
        i++
        continue
      } else if (char === ',') {
        currentRow.push(currentField.trim())
        currentField = ''
        i++
        continue
      } else if (char === '\r') {
        if (i + 1 < clean.length && clean[i + 1] === '\n') {
          i++
        }
        currentRow.push(currentField.trim())
        rows.push(currentRow)
        currentRow = []
        currentField = ''
        i++
        continue
      } else if (char === '\n') {
        currentRow.push(currentField.trim())
        rows.push(currentRow)
        currentRow = []
        currentField = ''
        i++
        continue
      } else {
        currentField += char
        i++
        continue
      }
    }
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim())
    rows.push(currentRow)
  }

  // Filter out any completely empty rows
  return rows.filter((r) => r.some((cell) => cell.length > 0))
}
